import type { Company } from '@paintscout/api'
import type { LeadSource } from '@paintscout/util/builder'
import { createCompany, getLeadSources } from '@paintscout/util/builder'
import type { DropdownSelectOption } from '@paintscout/ui'
import isNull from 'lodash/isNull'
import type { OptionsDocument, QuoteTag } from 'paintscout'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'

// Handle use/updating of company values for CompanyForm
export const useCompanyValues = ({
  company,
  options
}: {
  company: Partial<Company>
  options: OptionsDocument
}): {
  companyValues: Company
  setCompanyValues: (company: Company) => void
  companySources: LeadSource[]
  handleSourceChange: (option: DropdownSelectOption) => void
  handleFieldChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  mapAddressToValues: (results: any) => void
  handleTagChange: (tags: QuoteTag[]) => void
} => {
  const [values, setValues] = useState<Company>(getValues(company, options))

  const handleFieldChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = event.target
    setValues((prev) => ({ ...prev, [id]: value }))
  }, [])

  const handleTagChange = useCallback((tags: QuoteTag[]) => {
    setValues((prev) => ({ ...prev, tags: tags.map((tag) => tag.name) }))
  }, [])

  const handleSourceChange = useCallback((option: DropdownSelectOption) => {
    if (isNull(option) || !option) {
      setValues((prev) => ({ ...prev, lead_source: null }))
    } else {
      setValues((prev) => ({ ...prev, lead_source: option.value }))
    }
  }, [])

  const companySources = getLeadSources({ options, type: 'leadSources' }).map((source) => {
    return {
      value: source.label,
      label: source.label,
      name: source.name
    }
  })
  const mapAddressToValues = useCallback((results: any) => {
    // map searced address results to values on selection
    const { municipality, postalCode, region, address } = results
    setValues((prev) => {
      return {
        ...prev,
        city: municipality ?? prev.city,
        postal: postalCode ?? prev.postal,
        province: region ?? prev.province,
        address: address ?? prev.address
      }
    })
  }, [])

  useEffect(() => {
    setValues(getValues(company, options))
  }, [company])

  return {
    companyValues: values,
    setCompanyValues: setValues,
    companySources,
    handleFieldChange,
    mapAddressToValues,
    handleSourceChange,
    handleTagChange
  }
}

function getValues(company?: Partial<Company>, options?: OptionsDocument): Company {
  const defaultCompanyDetails = createCompany(company, options)
  return defaultCompanyDetails as Company
}
